import React, { useMemo, useState, useRef } from "react";
import {
  Tooltip,
  Typography,
  styled,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MUIButton from "../../../Component/UI/MUIButton/MUIButton";
import { OVERVIEW_LLM_LABELS } from "../../../utils/constant";
import { normalizeData } from "../../../utils/metric-sources";
import { ENTITY_LABELS } from "../../../utils/labelMapping";

const llmColorsMapping = {
  "gpt-4o": "#C7F0EC",
  Claude3: "#FF6F6F",
  Gemini: "#CEB4FF",
  llama3: "#FFCE6E",
  Perplexity: "#92B7FE",
  "llm-web": "#FEE7E6",
  "gpt-4o-web-search": "lightgreen",
  GeminiV25: "#CF9FFF",
  "gpt-5": "#B0E57C",
  "gpt-5-search-enabled": "#FFB6C1",
};

const colors = [
  "#C7F0EC",
  "#FF6F6F",
  "#CEB4FF",
  "#FFCE6E",
  "#92B7FE",
  "#FEE7E6",
  "#B0E57C",
  "#FFB6C1",
  "#ADD8E6",
  "#D8BFD8",
  "#FFD700",
  "#7FFFD4",
];

const fallbackColorMap = {};

const generateColorForLLM = (llm) => {
  if (!fallbackColorMap[llm]) {
    const colorIndex = Object.keys(fallbackColorMap).length % colors.length;
    fallbackColorMap[llm] = colors[colorIndex];
  }
  return fallbackColorMap[llm];
};

const processData = (data = [], sourceAlias = {}) => {
  if (!Array.isArray(data) || typeof sourceAlias !== "object") {
    return []; // Ensure function returns an empty array if inputs are invalid
  }

  return data
    .filter((obj) => obj && typeof obj === "object")
    .map((item) =>
      Object.fromEntries(
        Object.entries(item || {}).filter(
          ([_, value]) => value !== null && value !== undefined
        )
      )
    );
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const isLLMSearch = (llm) => {
  const lower = String(llm || "").toLowerCase();
  return lower.includes("search") || lower.includes("web") || lower.includes("perplexity") || lower.includes("(bd)") || lower.includes("_bd");
};

const sortAliases = (aliases) =>
  aliases.sort((a, b) => {
    const aV = a.http_status === "200";
    const bV = b.http_status === "200";
    if (aV !== bV) return bV ? 1 : -1;
    return (b.alias_absolute_count || 0) - (a.alias_absolute_count || 0);
  });

const mergeAliasesFromEntries = (entries) => {
  const map = {};
  entries.forEach((alias) => {
    const url = alias.source_alias_url;
    if (!url) return;
    if (!map[url]) {
      map[url] = { ...alias, alias_absolute_count: Number(alias.alias_absolute_count) || 0 };
    } else {
      map[url].alias_absolute_count += Number(alias.alias_absolute_count) || 0;
      if (alias.http_status === "200") map[url].http_status = "200";
    }
  });
  return sortAliases(Object.values(map));
};

// ── Tooltip content component (needs own useState for tab) ────────────────────

// A key is an LLM key if it appears in the known LLM maps or contains search/web keywords
const isKnownLLMKey = (key) =>
  key in (OVERVIEW_LLM_LABELS || {}) ||
  key in llmColorsMapping ||
  isLLMSearch(key);

function UrlList({ aliases, countLabel }) {
  return aliases.length > 0 ? (
    aliases.map((alias, index) => {
      const appeared = alias.alias_absolute_count || 0;
      return (
        <div key={index} style={{ marginBottom: 8 }}>
          <Typography
            component="a"
            href={alias.http_status === "200" ? alias.source_alias_url : undefined}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "block",
              color: alias.http_status === "200" ? "white" : "lightgray",
              textDecoration: alias.http_status === "200" ? "underline" : "none",
              cursor: alias.http_status === "200" ? "pointer" : "not-allowed",
              whiteSpace: "normal",
              wordBreak: "break-word",
              marginBottom: 4,
            }}
          >
            {alias.source_alias_url}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "white", minWidth: "50px" }}>
              [{Math.round(appeared)} {countLabel}]
            </Typography>
          </Box>
          {index < aliases.length - 1 && (
            <hr style={{ borderColor: "gray", margin: "6px 0" }} />
          )}
        </div>
      );
    })
  ) : (
    <Typography variant="caption" sx={{ color: "lightgray" }}>No data</Typography>
  );
}

function SourceTooltipContent({ item, fixedLlm }) {
  const pivotKeys = Object.keys(item).filter((k) => k !== "name");
  const keysAreLLMs = pivotKeys.some(isKnownLLMKey);

  // ── Always compute LLM-split data (hooks must be unconditional) ──────────────
  const searchAliasEntries = [];
  const nonSearchAliasEntries = [];
  const nonSearchLLMKeys = [];

  if (keysAreLLMs) {
    pivotKeys.forEach((llmKey) => {
      if (isLLMSearch(llmKey)) {
        searchAliasEntries.push(...(item[llmKey]?.aliases || []));
      } else {
        nonSearchAliasEntries.push(...(item[llmKey]?.aliases || []));
        if (!nonSearchLLMKeys.includes(llmKey)) nonSearchLLMKeys.push(llmKey);
      }
    });
  }

  const mergedSearch = mergeAliasesFromEntries(searchAliasEntries);
  const mergedNonSearch = mergeAliasesFromEntries(nonSearchAliasEntries);
  const hasBoth = mergedSearch.length > 0 && mergedNonSearch.length > 0;
  const defaultTab = hasBoth ? "citations" : mergedNonSearch.length > 0 ? "attributions" : "citations";

  // useState must be called unconditionally before any early return
  const [activeTab, setActiveTab] = useState(defaultTab);

  // ── Non-LLM compare (market / persona / category): use fixedLlm for classification ──
  if (!keysAreLLMs) {
    const allAliases = mergeAliasesFromEntries(
      pivotKeys.flatMap((k) => item[k]?.aliases || [])
    );
    const fixedIsNonSearch = !!fixedLlm && !isLLMSearch(fixedLlm);
    const fixedLlmName = OVERVIEW_LLM_LABELS?.[fixedLlm] || fixedLlm || "This model";
    const sectionLabel = fixedIsNonSearch ? "Source Attributions" : "Citations";

    return (
      <div onMouseDown={(e) => e.preventDefault()}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: "white", display: "block", mb: 0.5, fontSize: 13, pb: 0.5, borderBottom: "1px solid rgba(255,255,255,0.2)" }}
        >
          {sectionLabel}
        </Typography>
        {fixedIsNonSearch && (
          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.6, display: "block", fontSize: 11, wordBreak: "break-word", mb: 1, pb: 1, borderBottom: "1px solid rgba(255,255,255,0.15)" }}
          >
            <strong style={{ color: "white" }}>Note:</strong>{" "}
            {fixedLlmName} is a non-search-enabled model that answers from parametric (training)
            knowledge, not live web retrieval — so responses carry no true citations.
          </Typography>
        )}
        <UrlList aliases={allAliases} countLabel={sectionLabel} />
      </div>
    );
  }

  // ── LLM compare ──────────────────────────────────────────────────────────────
  const activeAliases = activeTab === "attributions" ? mergedNonSearch : mergedSearch;
  const countLabel = activeTab === "attributions" ? "Source Attributions" : "Citations";

  const nonSearchNames = nonSearchLLMKeys.map((k) => OVERVIEW_LLM_LABELS?.[k] || k).join(", ");
  const noteVerb = nonSearchLLMKeys.length === 1
    ? "is a non-search-enabled model that answers"
    : "are non-search-enabled models that answer";

  const tabBtn = (tab, label) => (
    <Box
      key={tab}
      component="button"
      onClick={() => setActiveTab(tab)}
      sx={{
        background: activeTab === tab ? "rgba(255,255,255,0.25)" : "transparent",
        border: "1px solid rgba(255,255,255,0.3)",
        borderRadius: "4px",
        color: "white",
        cursor: "pointer",
        fontSize: 11,
        fontWeight: activeTab === tab ? 700 : 400,
        px: 1,
        py: 0.5,
      }}
    >
      {label}
    </Box>
  );

  return (
    <div onMouseDown={(e) => e.preventDefault()}>
      {hasBoth && (
        <Box sx={{ display: "flex", gap: 0.5, mb: 1, pb: 0.5, borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
          {tabBtn("citations", "Citations")}
          {tabBtn("attributions", "Source Attributions")}
        </Box>
      )}

      {!hasBoth && (
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: "white", display: "block", mb: 0.5, fontSize: 13, pb: 0.5, borderBottom: "1px solid rgba(255,255,255,0.2)" }}
        >
          {defaultTab === "attributions" ? "Source Attributions" : "Citations"}
        </Typography>
      )}

      {activeTab === "attributions" && nonSearchLLMKeys.length > 0 && (
        <Typography
          variant="caption"
          sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.6, display: "block", fontSize: 11, wordBreak: "break-word", mb: 1, pb: 1, borderBottom: "1px solid rgba(255,255,255,0.15)" }}
        >
          <strong style={{ color: "white" }}>Note:</strong>{" "}
          {nonSearchNames} {noteVerb} from parametric (training) knowledge, not live web retrieval —
          so responses carry no true citations.
        </Typography>
      )}

      <UrlList aliases={activeAliases} countLabel={countLabel} />
    </div>
  );
}

// ── Scrollable tooltip (defined outside component to avoid recreation on render) ─

const ScrollableTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .MuiTooltip-tooltip`]: {
    maxHeight: 300,
    overflowY: "auto",
    maxWidth: 500,
    wordBreak: "break-word",
    padding: 8,
  },
});

// ── Source cell: controlled open so tab clicks never close the tooltip ───────────

function SourceCell({ item, fixedLlm }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const allAliases = Object.entries(item)
    .filter(([key]) => key !== "name")
    .flatMap(([_, llmData]) => llmData.aliases || []);

  const hasAnyUrl = allAliases.some((a) => !!a.source_alias_url);
  const isForbidden = !hasAnyUrl || allAliases.every((a) => a.http_status !== "200");
  const isReporting = window.location.search.includes("isReporting=true");

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  const triggerStyle = {
    color: isForbidden ? "gray" : "blue",
    cursor: isForbidden ? "not-allowed" : "pointer",
    textDecoration: isForbidden ? "none" : "underline",
  };

  if (isReporting) {
    return <Typography style={triggerStyle}>{item.name}</Typography>;
  }

  return (
    <ScrollableTooltip
      open={hasAnyUrl && open}
      title={
        hasAnyUrl ? (
          <Box onMouseDown={(e) => e.preventDefault()}>
            <SourceTooltipContent item={item} fixedLlm={fixedLlm} />
          </Box>
        ) : ""
      }
      placement="top-start"
      arrow
      disableHoverListener
      disableFocusListener
      disableTouchListener
      componentsProps={{
        tooltip: {
          onMouseEnter: cancelClose,
          onMouseLeave: scheduleClose,
        },
      }}
    >
      <Typography
        onMouseEnter={() => { cancelClose(); setOpen(true); }}
        onMouseLeave={scheduleClose}
        style={triggerStyle}
      >
        {item.name}
      </Typography>
    </ScrollableTooltip>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

const ReTableView = ({
  data,
  sourceAlias = [],
  type,
  isValidated = true,
  selectedInput,
  title,
  dropdownOptions = [], // [{value,label}, ...]
  selectedDropdownValues = [], // array of values (strings)
  onDropdownChange = () => { },
  isOverview = false,
}) => {
  // keep original normalize + state
  data = normalizeData(data, selectedInput?.llm);
  const [visibleRows, setVisibleRows] = useState(15);

  const filteredData = useMemo(
    () => processData(data, sourceAlias),
    [data, sourceAlias]
  );

  const sortedData = useMemo(() => {
    const getIsValidated = (item) =>
      Object.entries(item)
        .filter(([key]) => key !== "name")
        .flatMap(([_, llmData]) => llmData.aliases || [])
        .some((alias) => alias.http_status === "200");

    const getTotal = (item) =>
      Object.keys(item).reduce(
        (sum, key) =>
          key !== "name" && item[key]?.aliasTotalCount
            ? sum + Number(item[key]?.aliasTotalCount)
            : sum,
        0
      );

    return [...filteredData]
      .filter((obj) => !obj?.name.includes("vertexaisearch"))
      .sort((a, b) => {
        const validA = getIsValidated(a);
        const validB = getIsValidated(b);
        if (validA !== validB) return validB ? 1 : -1;
        return getTotal(b) - getTotal(a);
      });
  }, [filteredData]);

  const allLLMKeys = useMemo(() => {
    const keysSet = new Set();

    filteredData?.forEach((item) => {
      Object.keys(item || {}).forEach((key) => {
        if (key !== "name") keysSet.add(key);
      });
    });

    return Array.from(keysSet);
  }, [filteredData]);

  const handleShowMore = () => {
    setVisibleRows((prev) => prev + 15);
  };

  const maxTotalValue = useMemo(() => {
    return Math.max(
      ...sortedData.map((item) =>
        Object.keys(item).reduce(
          (sum, key) =>
            key !== "name" && item[key]?.aliasTotalCount
              ? sum + Number(item[key]?.aliasTotalCount)
              : sum,
          0
        )
      ),
      1
    );
  }, [sortedData]);

  const renderProgressBar = (item) => {
    const totalValue = Object.keys(item).reduce(
      (sum, key) =>
        key !== "name" && item[key]?.aliasTotalCount
          ? sum + Number(item[key]?.aliasTotalCount)
          : sum,
      0
    );

    const barWidth = `${(totalValue / maxTotalValue) * 100}%`;
    return (
      <div
        style={{
          display: "flex",
          height: 10,
          borderRadius: 5,
          overflow: "hidden",
          width: barWidth,
          backgroundColor: "#e0e0e0",
          position: "relative",
          zIndex: 1,
        }}
      >
        {allLLMKeys?.map((llm, i) => {
          const count = item[llm]?.aliasTotalCount;
          if (!count) return null;
          return (
            <Tooltip
              key={i}
              title={
                <div style={{ textAlign: "left" }}>
                  <Typography style={{ fontSize: 14, fontWeight: "bold" }}>
                    {OVERVIEW_LLM_LABELS?.[llm] || llm}
                  </Typography>
                  <div
                    style={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      backgroundColor:
                        llmColorsMapping?.[llm] || generateColorForLLM(llm),
                      marginRight: 5,
                      borderRadius: "50%",
                    }}
                  />
                  <Typography style={{ display: "inline", fontSize: 14 }}>
                    {count}
                  </Typography>
                </div>
              }
              arrow
              placement="top"
            >
              <div
                style={{
                  width: `${(count / totalValue) * 100}%`,
                  backgroundColor:
                    llmColorsMapping?.[llm] || generateColorForLLM(llm),
                  height: "100%",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          );
        })}
      </div>
    );
  };

  const renderSourceCell = (item) => <SourceCell item={item} fixedLlm={selectedInput?.llm} />;

  const isAllNonSearch = useMemo(() => {
    const keysAreLLMs = allLLMKeys.some(isKnownLLMKey);
    if (keysAreLLMs) {
      return allLLMKeys.length > 0 && allLLMKeys.every((k) => !isLLMSearch(k));
    }
    // non-LLM compare mode (market/persona/category): classify by the fixed LLM
    return !!selectedInput?.llm && !isLLMSearch(selectedInput.llm);
  }, [allLLMKeys, selectedInput]);

  const headers = [
    { label: isAllNonSearch ? "Source Attributions" : "Source", width: "25%" },
    { label: isAllNonSearch ? "Source Attributions Frequency" : "Source Frequency", width: "30%" },
  ];
  if (isValidated) {
    headers.push({ label: "Validated", width: "15%" });
  }

  // Dropdown rendering (multi-select) inside title area
  const renderDropdown = () => {
    if (!dropdownOptions || dropdownOptions.length === 0) return null;

    // Ensure selectedDropdownValues is an array
    const selectedArr = Array.isArray(selectedDropdownValues)
      ? selectedDropdownValues
      : [];

    const handleChange = (event) => {
      const val = event.target.value;
      // MUI returns array for multiple select
      onDropdownChange(val);
    };

    const handleChipDelete = (valueToRemove) => (event) => {
      event.stopPropagation();
      event.preventDefault();

      const newSelectedValues = selectedArr.filter(val => val !== valueToRemove);
      onDropdownChange(newSelectedValues);
    };

    const renderSelectedChips = (selected) => {
      if (!selected || selected.length === 0) {
        return <span style={{ color: '#9ca3af' }}>None</span>;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
          }}
        >
          {selected.map((selectedValue) => {
            const option = dropdownOptions.find(opt => opt.value === selectedValue);
            const displayName = option ? option.label : selectedValue;

            return (
              <Chip
                key={selectedValue}
                label={displayName}
                size="small"
                onDelete={handleChipDelete(selectedValue)}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                sx={{
                  height: "auto",
                  maxWidth: "100%",
                  margin: "2px 0",
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: 1.3,
                    paddingTop: "4px",
                    paddingBottom: "4px",
                    paddingRight: "4px",
                  },
                  "& .MuiChip-deleteIcon": {
                    fontSize: "18px",
                    margin: "0 4px 0 -2px",
                  }
                }}
              />
            );
          })}
        </Box>
      );
    };

    return (
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel sx={{ fontSize: 13 }}>Source Types</InputLabel>
        <Select
          multiple
          value={selectedArr}
          onChange={handleChange}
          renderValue={renderSelectedChips}
          label="Source Types"
          sx={{
            minHeight: "40px",
            "& .MuiSelect-select": {
              paddingTop: "6px",
              paddingBottom: "6px",
              display: "flex",
              alignItems: "center",
              minHeight: "28px",
            }
          }}
        >
          {dropdownOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              <Checkbox checked={selectedArr.indexOf(opt.value) > -1} />
              <ListItemText primary={opt.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <>

      <div
        className="mt-2 mb-2 d-flex align-items-center justify-content-center"
        style={{ gap: "10px", flexWrap: "wrap" }}
      >
        {!isOverview && (
          <h5 className="m-0 text-center">
            {title || `${type} Query Sources`}
          </h5>
        )}
        {/* dropdown placed next to the title */}
        {renderDropdown()}
      </div>


      <div
        id={(type === ENTITY_LABELS.brand ? "brand" : type === ENTITY_LABELS.product ? "product" : type.toLowerCase()) + "_id"}
        // id={type.toLowerCase() + "_id"}
        style={{
          maxHeight: "500px",
          overflowY: "auto",
          position: "relative",
        }}
      >
        <table className="table table-striped" style={{ width: "100%" }}>
          <thead
            style={{
              position: "sticky",
              top: 0,
              background: "white",
              zIndex: 2,
            }}
          >
            <tr className="prdtblHeader">
              {headers.map((header, index) => (
                <th
                  key={header.label}
                  style={{
                    width: header.width,
                    textAlign: index === 0 ? "left" : "center",
                    padding: "10px",
                  }}
                >
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!!sortedData.length ? (
              sortedData.slice(0, visibleRows).map((item, index) => (
                <tr key={index}>
                  <td style={{ width: "25%", textAlign: "left" }}>
                    {renderSourceCell(item, sourceAlias[item.name] || [])}
                  </td>
                  <td style={{ width: "30%", textAlign: "center" }}>
                    {renderProgressBar(item)}
                  </td>
                  {isValidated ? (
                    <td style={{ width: "15%", textAlign: "center" }}>
                      {Object.entries(item)
                        .filter(([key]) => key !== "name")
                        .flatMap(([_, llmData]) => llmData.aliases || [])
                        ?.every((link) => link.http_status !== "200") ? (
                        <CancelIcon style={{ color: "#F59E0B" }} />
                      ) : (
                        <CheckCircleIcon style={{ color: "#36CFC2" }} />
                      )}
                    </td>
                  ) : null}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={4}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {visibleRows < sortedData.length && (
          <div
            style={{ textAlign: "center", padding: "10px", fontWeight: "400" }}
          >
            <MUIButton variant="text" onClick={handleShowMore}>
              Load More
            </MUIButton>
          </div>
        )}
      </div>
    </>
  );
};

export default ReTableView;
